import { PlusIcon } from '@heroicons/react/24/outline';
import { ComponentType, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AddButtonStyled } from './AddButton.styled';

interface AddButtonProps {
    onClick: () => void;
    isSmall?: boolean;
    /** Tekst przycisku. Domyślnie „Dodaj nowy". */
    label?: string;
    /** Gdy true — renderuje sam plus (bez tekstu), np. w ciasnych miejscach. */
    iconOnly?: boolean;
    disabled?: boolean;
    /** Kolor przycisku. `light` = ten sam kształt, biały (akcja obok „Dodaj nowy"). */
    variant?: 'dark' | 'light';
    /** Własna ikona zamiast plusa (np. magazyn). Dostaje klasę `.icon` ze stylów. */
    icon?: ComponentType<{ className?: string; strokeWidth?: number }>;
    /** Stabilny selektor dla testów E2E (nazwa dostępna zależy od języka UI). */
    'data-testid'?: string;
}

export const AddButton = forwardRef<HTMLButtonElement, AddButtonProps>(({
  onClick, isSmall = false, label, iconOnly = false, disabled = false,
  variant = 'dark', icon: Icon = PlusIcon, 'data-testid': testId,
}, ref) => {
  const { t } = useTranslation();
  const text = label ?? t('common.addNew', 'Dodaj nowy');

  return (
    <AddButtonStyled
      ref={ref}
      onClick={onClick}
      $isSmall={isSmall}
      $iconOnly={iconOnly}
      $variant={variant}
      type="button"
      disabled={disabled}
      aria-label={text}
      data-testid={testId}
    >
      <Icon
        className="icon"
        strokeWidth={2.5}
      />
      {!iconOnly && <span className="label">{text}</span>}
    </AddButtonStyled>
  );
});

AddButton.displayName = 'AddButton';
